<template>
  <div class="row">
    <div class="col-lg-5 col-md-6 col-sm-12">
      <div class="mb-3">
        <label>Cantidad mínima <span class="text-danger">*</span></label>
        <vee-field
          class="form-control"
          name="minimum"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="Cantidad mínima"
          v-model="localMinimum"
          @input="handleMinimumInput"
        />
        <ErrorMessage class="text-danger" name="minimum" />
      </div>
    </div>

    <div class="col-lg-5 col-md-6 col-sm-12">
      <div class="mb-3">
        <label>Cantidad máxima <span class="text-danger">*</span></label>
        <vee-field
          class="form-control"
          name="maximum"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="Cantidad máxima"
          v-model="localMaximum"
          @input="handleMaximumInput"
        />
        <ErrorMessage class="text-danger" name="maximum" />
        <small v-if="placeCapacity > 0 && withLimitControl" class="text-danger">
          Máximo permitido: {{ placeCapacity }} (según capacidad del lugar)
        </small>
        <p v-if="!withLimitControl">
          <small class="text-danger">Control de capacidad desactivado</small>
          -
          <small class="text-dark">
            No se validará la capacidad máxima del lugar pero si la ingresada.
          </small>
        </p>
      </div>
    </div>

    <div class="col-lg-2 col-md-12 col-sm-12">
      <div class="mb-3">
        <label class="col-form-label m-r-10">Con control de capacidad?</label>
        <div class="media-body text-center icon-state switch-outline">
          <label class="switch">
            <input
              type="checkbox"
              v-model="localWithLimitControl"
              @change="handleSwitchChange"
            />
            <span class="switch-state bg-primary"></span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps({
  minimum: {
    type: [String, Number],
    default: "",
  },
  maximum: {
    type: [String, Number],
    default: "",
  },
  placeCapacity: {
    type: Number,
    default: 0,
  },
  withLimitControl: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "update:minimum",
  "update:maximum",
  "update:withLimitControl",
]);

const localMinimum = ref(props.minimum);
const localMaximum = ref(props.maximum);
const localWithLimitControl = ref(props.withLimitControl);

const handleMinimumInput = (event) => {
  let value = event.target.value;
  value = value.replace(/[^0-9]/g, "");

  if (value.length > 1 && value.startsWith("0")) {
    value = value.replace(/^0+/, "");
  }

  if (value === "" || value === "0") {
    value = "1";
  }

  localMinimum.value = value;
  emit("update:minimum", value);
};

const handleMaximumInput = (event) => {
  let value = event.target.value;
  value = value.replace(/[^0-9]/g, "");

  if (value.length > 1 && value.startsWith("0")) {
    value = value.replace(/^0+/, "");
  }

  localMaximum.value = value;
  emit("update:maximum", value);
};

const handleSwitchChange = () => {
  emit("update:withLimitControl", localWithLimitControl.value);
};

watch(
  () => props.minimum,
  (newVal) => {
    localMinimum.value = newVal;
  }
);

watch(
  () => props.maximum,
  (newVal) => {
    localMaximum.value = newVal;
  }
);

watch(
  () => props.withLimitControl,
  (newVal) => {
    localWithLimitControl.value = newVal;
  }
);

const validate = () => {
  const min = parseInt(localMinimum.value);
  const max = parseInt(localMaximum.value);

  if (isNaN(min) || min === 0) {
    return {
      valid: false,
      message: "La cantidad mínima no puede ser 0.",
    };
  }

  if (isNaN(max) || max < min) {
    return {
      valid: false,
      message: "La cantidad máxima no puede ser menor que la mínima.",
    };
  }

  if (
    localWithLimitControl.value &&
    props.placeCapacity > 0 &&
    max > props.placeCapacity
  ) {
    return {
      valid: false,
      message: `La cantidad máxima (${max}) no puede ser mayor a la capacidad del lugar (${props.placeCapacity}).`,
    };
  }

  return { valid: true };
};

const clearAll = () => {
  localMinimum.value = "";
  localMaximum.value = "";
  localWithLimitControl.value = true;
  emit("update:minimum", "");
  emit("update:maximum", "");
  emit("update:withLimitControl", true);
};

defineExpose({
  validate,
  clearAll,
});
</script>
